import axios from 'axios'
import useAuthStore from '../stores/authStore'
import { API_BASE_URL } from '../config/apiBaseUrl'

// Create axios instance
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 30000 // Increased to 30s as global default for cloud stability
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const { accessToken } = useAuthStore.getState()

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // If error is 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      const { refreshAccessToken } = useAuthStore.getState()
      const refreshed = await refreshAccessToken()

      if (refreshed) {
        const { accessToken } = useAuthStore.getState()
        originalRequest.headers.Authorization = `Bearer ${accessToken}`
        return api(originalRequest)
      }
    }

    return Promise.reject(error)
  }
)

// Auth API
export const authApi = {
  login: (usuario, password) => api.post('/auth/login', { usuario, password }),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  refresh: (refreshToken) => api.post('/auth/refresh', { refreshToken })
}

// Personas API
export const personasApi = {
  search: (params) => api.get('/personas', { params }),
  opcionesCruces: () => api.get('/personas/cruces/opciones'),
  entidades: (params) => api.get('/personas/entidades', { params }),
  getById: (id) => api.get(`/personas/${id}`),
  getFbMatch: (id) => api.get(`/personas/${id}/fb-match`),
  create: (data) => api.post('/personas', data),
  update: (id, data) => api.put(`/personas/${id}`, data),
  remove: (id) => api.delete(`/personas/${id}`),
  getHistorial: (id) => api.get(`/personas/${id}/historial`),
  // Contrato de dominio versionado para la Vista 360 de Persona/Hogar.
  getContexto: (id) => api.get(`/v1/personas/${id}/contexto`)
}

// Asistencias API
export const asistenciasApi = {
  list: (params) => api.get('/asistencias', { params }),
  create: (data) => api.post('/asistencias', data),
  update: (id, data) => api.put(`/asistencias/${id}`, data),
  registrarHogar: (data) => api.post('/asistencias/hogar/registro', data)
}

// Dashboard API
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
  getPanel: () => api.get('/dashboard/panel'),
  getPorOrganismo: () => api.get('/dashboard/por-organismo'),
  getPorTipo: () => api.get('/dashboard/por-tipo'),
  getEvolucionMensual: (params) => api.get('/dashboard/evolucion-mensual', { params }),
  getActividadSemanal: () => api.get('/dashboard/actividad-semanal')
}

// Audit / Logs API
export const auditApi = {
  list: (params) => api.get('/audit', { params }),
  options: () => api.get('/audit/options'),
  online: () => api.get('/audit/online'),
  stats: () => api.get('/audit/stats'),
}

// Analytics API
export const analyticsApi = {
  getDashboard: (params) => api.get('/dashboard/analytics/dashboard', { params }),
  getMapa: (params) => api.get('/dashboard/analytics/mapa', { params }),
  getBarriosGeoJson: () => api.get('/dashboard/analytics/barrios-geojson'),
  getBeneficiarios: (params) => api.get('/dashboard/analytics/beneficiarios', { params }),
  // Residencia: beneficiarios sin domicilio electoral en San Roque (padrón 2025).
  getResidencia: (params) => api.get('/dashboard/analytics/residencia', { params }),
  exportResidencia: (params) => api.get('/dashboard/analytics/residencia/export', { params, responseType: 'blob' }),
  exportExcel: (params) => api.get('/dashboard/analytics/export', {
    params,
    responseType: 'blob'
  }),
  // §8.3 Duplicados Cruzados Interinstitucionales
  getDuplicadosCruzados: (params) => api.get('/dashboard/analytics/duplicados-cruzados', { params }),
  // §3.2 Detección de Anomalías (Z-Score)
  getAnomalias: (params) => api.get('/dashboard/analytics/anomalias', { params }),
  getOperationalKpis: (params) => api.get('/v1/analytics/kpis', { params }),
  createSnapshot: (data) => api.post('/v1/analytics/snapshots', data),
  listSnapshots: (params) => api.get('/v1/analytics/snapshots', { params })
}

// Uploads API (S3/R2)
// Documentos de una persona: DNI (frente/dorso) y recetas. El archivo se pide
// con la sesion (responseType blob) porque un DNI no tiene URL publica.
export const documentosApi = {
  list: (personaId) => api.get('/documentos', { params: { persona_id: personaId } }),
  upload: (personaId, tipo, file, asistenciaId = null) => {
    const form = new FormData()
    form.append('persona_id', personaId)
    form.append('tipo', tipo)
    if (asistenciaId) form.append('asistencia_id', asistenciaId)
    form.append('archivo', file)
    return api.post('/documentos', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000
    })
  },
  archivo: (id) => api.get(`/documentos/${id}/archivo`, { responseType: 'blob' }),
  remove: (id) => api.delete(`/documentos/${id}`)
}

// Comedores comunitarios: sedes, padron por sede, asistencia diaria, mercaderia,
// gastos y tablero de costos.
export const comedoresApi = {
  sedes: () => api.get('/comedores'),
  cruces: (params) => api.get('/comedores/cruces', { params }),
  guardarSede: (data, id = null) => (id ? api.put(`/comedores/${id}`, data) : api.post('/comedores', data)),
  beneficiarios: (comedorId) => api.get(`/comedores/${comedorId}/beneficiarios`),
  guardarBeneficiario: (data, { comedorId, id } = {}) => (id
    ? api.put(`/comedores/beneficiarios/${id}`, data)
    : api.post(`/comedores/${comedorId}/beneficiarios`, data)),
  bajaBeneficiario: (id, params) => api.delete(`/comedores/beneficiarios/${id}`, { params }),
  eliminarBeneficiario: (id, { fisico = true } = {}) => api.delete(`/comedores/beneficiarios/${id}`, { params: { fisico } }),
  eliminarBeneficiariosLote: (comedorId, ids, { fisico = true } = {}) => api.post(`/comedores/${comedorId}/beneficiarios/eliminar-lote`, { ids, fisico }),
  verificar: (params) => api.get('/comedores/verificar', { params }),
  importarPadron: (comedorId, filas, aplicar) => api.post(`/comedores/${comedorId}/beneficiarios/importar`, { filas, aplicar }),
  asistencia: (comedorId, fecha) => api.get(`/comedores/${comedorId}/asistencia`, { params: fecha ? { fecha } : {} }),
  guardarAsistencia: (comedorId, fecha, marcas) => api.put(`/comedores/${comedorId}/asistencia`, { fecha, marcas }),
  remitos: (comedorId, mes) => api.get(`/comedores/${comedorId}/remitos`, { params: { mes } }),
  firmarRemito: (comedorId, data) => api.put(`/comedores/${comedorId}/remitos/firma`, data),
  crearRemito: (comedorId, data) => api.post(`/comedores/${comedorId}/remitos`, data),
  loteRemitos: (comedorId, filas) => api.post(`/comedores/${comedorId}/remitos/lote`, { filas }),
  borrarRemito: (id) => api.delete(`/comedores/remitos/${id}`),
  gastos: (comedorId, mes) => api.get(`/comedores/${comedorId}/gastos`, { params: { mes } }),
  crearGasto: (comedorId, data) => api.post(`/comedores/${comedorId}/gastos`, data),
  loteGastos: (comedorId, filas) => api.post(`/comedores/${comedorId}/gastos/lote`, { filas }),
  borrarGasto: (id) => api.delete(`/comedores/gastos/${id}`),
  tablero: (mes) => api.get('/comedores/tablero', { params: { mes } })
}

export const capacitacionesApi = {
  list: () => api.get('/capacitaciones'),
  padron: () => api.get('/capacitaciones/padron'),
  persona: (dni) => api.post('/capacitaciones/persona', { dni }),
  detalle: (id) => api.get(`/capacitaciones/${id}`),
  guardar: (data, id = null) => (id ? api.put(`/capacitaciones/${id}`, data) : api.post('/capacitaciones', data)),
  baja: (id) => api.delete(`/capacitaciones/${id}`),
  guardarParticipantes: (id, participantes) => api.put(`/capacitaciones/${id}/participantes`, { participantes })
}

export const kitFemeninoApi = {
  periodos: () => api.get('/kit-femenino/periodos'),
  mes: (periodo) => api.get(`/kit-femenino/periodos/${periodo}`),
  guardar: (periodo, kits) => api.put(`/kit-femenino/periodos/${periodo}`, { kits }),
  persona: (dni) => api.post('/kit-femenino/persona', { dni }),
  barrios: () => api.get('/kit-femenino/barrios')
}

export const produccionApi = {
  list: (params) => api.get('/produccion/entregas', { params }),
  padron: () => api.get('/produccion/padron'),
  analitica: () => api.get('/produccion/analitica'),
  persona: (dni) => api.post('/produccion/persona', { dni }),
  barrios: () => api.get('/produccion/barrios'),
  tipos: () => api.get('/produccion/tipos'),
  agregarTipo: (nombre) => api.post('/produccion/tipos', { nombre }),
  domicilio: (personaId, datos) => api.put(`/produccion/persona/${personaId}/domicilio`, datos),
  guardar: (entregas) => api.put('/produccion/entregas', { entregas }),
  baja: (id) => api.delete(`/produccion/entregas/${id}`)
}

export const comedorStockApi = {
  list: () => api.get('/comedores/stock'),
  alertas: () => api.get('/comedores/stock/alertas'),
  guardar: (items) => api.put('/comedores/stock', { items }),
  agregarArticulo: (nombre) => api.post('/comedores/stock/articulos', { nombre }),
  renombrarArticulo: (id, nombre) => api.put(`/comedores/stock/articulos/${id}`, { nombre }),
  bajaArticulo: (id) => api.delete(`/comedores/stock/articulos/${id}`),
  movimientos: () => api.get('/comedores/stock/movimientos')
}

export const reclamosApi = {
  list: (params) => api.get('/reclamos', { params }),
  detalle: (id) => api.get(`/reclamos/${id}`),
  guardar: (data, id = null) => (id ? api.put(`/reclamos/${id}`, data) : api.post('/reclamos', data)),
  cambiarEstado: (id, data) => api.put(`/reclamos/${id}/estado`, data),
  identificar: (id, data) => api.put(`/reclamos/${id}/persona`, data)
}

export const institucionesApi = {
  list: (params) => api.get('/instituciones', { params }),
  guardar: (data, id = null) => (id ? api.put(`/instituciones/${id}`, data) : api.post('/instituciones', data)),
  detalle: (id) => api.get(`/instituciones/${id}`),
  historial: (id, params) => api.get(`/instituciones/${id}/historial`, { params }),
  guardarAsistencia: (institucionId, data, aid = null) => (aid
    ? api.put(`/instituciones/${institucionId}/asistencias/${aid}`, data)
    : api.post(`/instituciones/${institucionId}/asistencias`, data)),
  borrarAsistencia: (institucionId, aid, motivo) => api.delete(`/instituciones/${institucionId}/asistencias/${aid}`, { data: { motivo } }),
  ranking: (params) => api.get('/instituciones/ranking', { params })
}

export const uploadsApi = {
  uploadMany: (files) => {
    const form = new FormData()
    for (const f of files) form.append('files', f)
    return api.post('/uploads', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000
    })
  }
}

// Family API
// Hogar (grupo familiar): integrantes, referente y domicilio. Los vínculos de
// parentesco siguen en familyApi y se leen en los dos sentidos.
export const grupoFamiliarApi = {
  dePersona: (personaId) => api.get(`/personas/${personaId}/grupo`),
  crear: (referenteId) => api.post('/grupos-familiares', { referente_id: referenteId }),
  actualizar: (grupoId, data) => api.put(`/grupos-familiares/${grupoId}`, data),
  agregarMiembro: (grupoId, data) => api.post(`/grupos-familiares/${grupoId}/miembros`, data),
  quitarMiembro: (grupoId, personaId) => api.delete(`/grupos-familiares/${grupoId}/miembros/${personaId}`)
}

export const familyApi = {
  getNucleus: (personaId) => api.get(`/personas/${personaId}/familia`),
  addMember: (data) => api.post('/familia', data),
  updateMember: (id, data) => api.put(`/familia/${id}`, data),
  removeMember: (id) => api.delete(`/familia/${id}`)
}

// Demo data API (admin)
export const demoApi = {
  seed: ({ count = 1200, mode = 'demo', includeFamilies = true } = {}) =>
    api.post('/demo/seed', { count, mode, includeFamilies }, { timeout: 180000 }),
  stats: () => api.get('/demo/stats'),
  purge: (lote) =>
    api.delete('/demo/purge', {
      data: lote ? { lote } : {}
    })
}

// Config API (admin + shared)
export const configApi = {
  getAssistanceTypes: () => api.get('/config/assistance-types'),
  updateAssistanceTypes: (data) => api.put('/config/assistance-types', data),
}

// Soporte Técnico API
export const soporteApi = {
  list: () => api.get('/soporte'),
  // create/addMessage aceptan imágenes opcionales (capturas). Si hay archivos se
  // envía multipart/form-data; si no, JSON como antes.
  create: (consulta, imagenes = []) => {
    if (imagenes && imagenes.length) {
      const fd = new FormData()
      fd.append('consulta', consulta)
      imagenes.forEach((f) => fd.append('imagenes', f))
      return api.post('/soporte', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
    }
    return api.post('/soporte', { consulta })
  },
  getById: (id) => api.get(`/soporte/${id}`),
  addMessage: (id, mensaje, imagenes = []) => {
    if (imagenes && imagenes.length) {
      const fd = new FormData()
      fd.append('mensaje', mensaje || '')
      imagenes.forEach((f) => fd.append('imagenes', f))
      return api.post(`/soporte/${id}/mensaje`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
    }
    return api.post(`/soporte/${id}/mensaje`, { mensaje })
  },
  updateEstado: (id, estado) => api.put(`/soporte/${id}/estado`, { estado }),
}

// Import API
export const importApi = {
  syncTransito: () => api.post('/import/transito-sync'),
}

// Viajes API
export const viajesApi = {
  getWeek: (params) => api.get('/viajes/week', { params }),
  getPadron: (params) => api.get('/viajes/padron', { params }),
  saveWeek: (data) => api.post('/viajes/save-week', data),
  import: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/viajes/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  export: (params) => api.get('/viajes/export', { params, responseType: 'blob' }),
  remindPreview: (params) => api.get('/viajes/remind-preview', { params }),
  remindDay: (data) => api.post('/viajes/remind-day', data)
};

// WhatsApp API (EXPERIMENTAL)
export const whatsappApi = {
  getSession: () => api.get('/whatsapp/session'),
  connectSession: () => api.post('/whatsapp/session/connect'),
  disconnectSession: () => api.post('/whatsapp/session/disconnect'),
  resetSession: () => api.post('/whatsapp/session/reset'),
  getQr: () => api.get('/whatsapp/session/qr'),
  getConversaciones: () => api.get('/whatsapp/conversaciones'),
  getThread: (chatId) => api.get(`/whatsapp/conversaciones/${encodeURIComponent(chatId)}/mensajes`),
  sendMessage: (data) => api.post('/whatsapp/mensajes', data),
  sendMedia: (formData) => api.post('/whatsapp/mensajes/media', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadMedia: (formData) => api.post('/whatsapp/upload-media', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getHistorial: (params) => api.get('/whatsapp/historial', { params }),
  previewCampana: (data) => api.post('/whatsapp/campanas/preview', data),
  enviarCampana: (data) => api.post('/whatsapp/campanas/enviar', data),
  estadoCampana: (batchId) => api.get(`/whatsapp/campanas/${batchId}/estado`),
  getCampanas: () => api.get('/whatsapp/campanas'),
  pausarCampana: (id) => api.post(`/whatsapp/campanas/${id}/pausar`),
  reanudarCampana: (id) => api.post(`/whatsapp/campanas/${id}/reanudar`),
  cancelarCampana: (id) => api.post(`/whatsapp/campanas/${id}/cancelar`),
  reintentarFallidos: (id) => api.post(`/whatsapp/campanas/${id}/reintentar-fallidos`),
  // Multi-Líneas / Multi-SIM
  listLineas: () => api.get('/whatsapp/lineas'),
  createLinea: (data) => api.post('/whatsapp/lineas', data),
  deleteLinea: (id) => api.delete(`/whatsapp/lineas/${id}`),
  getLineaQr: (sessionName) => api.get(`/whatsapp/lineas/${sessionName}/qr`),
  connectLinea: (sessionName) => api.post(`/whatsapp/lineas/${sessionName}/connect`),
  disconnectLinea: (sessionName) => api.post(`/whatsapp/lineas/${sessionName}/disconnect`),
  // CRM Municipal
  getCrmStats: () => api.get('/whatsapp/crm/stats'),
  updateChatCrm: (chatId, data) => api.put(`/whatsapp/conversaciones/${encodeURIComponent(chatId)}/crm`, data),
  listNotasInternas: (chatId) => api.get(`/whatsapp/conversaciones/${encodeURIComponent(chatId)}/notas`),
  createNotaInterna: (chatId, data) => api.post(`/whatsapp/conversaciones/${encodeURIComponent(chatId)}/notas`, data),
  getCiudadano360ByChat: (chatId) => api.get(`/whatsapp/conversaciones/${encodeURIComponent(chatId)}/ciudadano-360`)
};

// Caja Celíacos API
export const cajaCeliacosApi = {
  import: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/caja-celiacos/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getBeneficiarios: (params) => api.get('/caja-celiacos/beneficiarios', { params }),
  saveGrid: (data) => api.post('/caja-celiacos/save-grid', data),
  destildarTodo: () => api.post('/caja-celiacos/destildar-todo'),
  updateBeneficiario: (id, data) => api.put(`/caja-celiacos/beneficiarios/${id}`, data),
  removeBeneficiario: (id) => api.delete(`/caja-celiacos/beneficiarios/${id}`),
  export: () => api.get('/caja-celiacos/export', { responseType: 'blob' })
};

// Tarjeta Sapucay API
export const tarjetaSapucayApi = {
  import: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/tarjeta-sapucay/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getBeneficiarios: (params) => api.get('/tarjeta-sapucay/beneficiarios', { params }),
  sync: () => api.post('/tarjeta-sapucay/sync'),
  saveGrid: (data) => api.post('/tarjeta-sapucay/save-grid', data),
  destildarTodo: () => api.post('/tarjeta-sapucay/destildar-todo'),
  updateBeneficiario: (id, data) => api.put(`/tarjeta-sapucay/beneficiarios/${id}`, data),
  removeBeneficiario: (id) => api.delete(`/tarjeta-sapucay/beneficiarios/${id}`),
  export: () => api.get('/tarjeta-sapucay/export', { responseType: 'blob' })
};

// Tarjeta Mamá Mbareté API — embarazadas, cobertura provincial, $50.000/mes
export const mamaMbareteApi = {
  import: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/tarjeta-mama-mbarete/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getBeneficiarios: (params) => api.get('/tarjeta-mama-mbarete/beneficiarios', { params }),
  sync: () => api.post('/tarjeta-mama-mbarete/sync'),
  updateBeneficiario: (id, data) => api.put(`/tarjeta-mama-mbarete/beneficiarios/${id}`, data),
  removeBeneficiario: (id) => api.delete(`/tarjeta-mama-mbarete/beneficiarios/${id}`),
  export: () => api.get('/tarjeta-mama-mbarete/export', { responseType: 'blob' })
};

// Vales para Retirar API (admin)
export const valesApi = {
  proveedoresSaldo: (mes, rubro) => api.get('/vales/proveedores-saldo', { params: { mes, ...(rubro ? { rubro } : {}) } }),
  updateModuloMasivo: (precio_modulo) => api.put('/vales/proveedores-modulo-masivo', { precio_modulo }),
  createProveedor: (data) => api.post('/vales/proveedores', data),
  updateProveedor: (id, data) => api.put(`/vales/proveedores/${id}`, data),
  deleteProveedor: (id) => api.delete(`/vales/proveedores/${id}`),
  list: (params) => api.get('/vales', { params }),
  stats: (params) => api.get('/vales/stats', { params }),
  getBudgetLimit: () => api.get('/vales/budget-limit'),
  updateBudgetLimit: (limit) => api.put('/vales/budget-limit', { limit }),
  track: (payload) => api.post('/vales/track', payload),
  getById: (id) => api.get(`/vales/${id}`),
  create: (data) => api.post('/vales', data),
  update: (id, data) => api.put(`/vales/${id}`, data),
  remove: (id) => api.delete(`/vales/${id}`),
  addNota: (id, nota) => api.post(`/vales/${id}/notas`, { nota }),
  import: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/vales/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  export: () => api.get('/vales/export', { responseType: 'blob' })
};

// Mesa de Entrada API
export const mesaEntradaApi = {
  list: (params) => api.get('/mesa-entrada', { params }),
  stats: (params) => api.get('/mesa-entrada/stats', { params }),
  getById: (id) => api.get(`/mesa-entrada/${id}`),
  create: (data) => api.post('/mesa-entrada', data),
  update: (id, data) => api.put(`/mesa-entrada/${id}`, data),
  remove: (id) => api.delete(`/mesa-entrada/${id}`),
  addNota: (id, nota) => api.post(`/mesa-entrada/${id}/notas`, { nota })
};

// Inundaciones / Evacuados API
export const inundacionesApi = {
  getEventos: () => api.get('/inundaciones/eventos'),
  createEvento: (data) => api.post('/inundaciones/eventos', data),
  getEvacuados: (params) => api.get('/inundaciones/evacuados', { params }),
  createEvacuado: (data) => api.post('/inundaciones/evacuados', data),
  updateEvacuado: (id, data) => api.put(`/inundaciones/evacuados/${id}`, data),
  deleteEvacuado: (id) => api.delete(`/inundaciones/evacuados/${id}`),
  getStats: (eventoId) => api.get(`/inundaciones/stats/${eventoId}`),
  exportExcel: (eventoId) => api.get(`/inundaciones/export/${eventoId}`, { responseType: 'blob' })
};

// Emprendedores API
export const emprendedoresApi = {
  list: (params) => api.get('/emprendedores', { params }),
  stats: (params) => api.get('/emprendedores/stats', { params }),
  create: (data) => api.post('/emprendedores', data),
  update: (id, data) => api.put(`/emprendedores/${id}`, data),
  grantHelp: (id, data) => api.post(`/emprendedores/${id}/ayudas`, data),
  getHelpHistory: (id) => api.get(`/emprendedores/${id}/ayudas`)
};

// El Niño (relevamiento por inundación, sistema aparte)
export const elninoApi = {
  resumen: (refrescar) => api.get(`/elnino/resumen${refrescar ? '?refrescar=1' : ''}`),
  personas: (refrescar) => api.get(`/elnino/personas${refrescar ? '?refrescar=1' : ''}`),
  // Guarda el padrón de El Niño en la base de Acción Social y lo cruza por DNI.
  sincronizar: () => api.post('/elnino/sincronizar')
};

// Observatorio API
export const observatorioApi = {
  getStats: () => api.get('/observatorio/stats'),
  getAlerts: () => api.get('/observatorio/alerts'),
  getPersonaGraph: (id) => api.get(`/observatorio/persona/${id}`),
  syncGraph: () => api.post('/observatorio/sync')
};

export const osintPersonaApi = {
  context: (personaId) => api.get(`/osint/persona/${encodeURIComponent(personaId)}/context`),
  investigations: (personaId) => api.get(`/osint/persona/${encodeURIComponent(personaId)}/investigations`),
  manuales: (personaId) => api.get(`/osint/persona/${encodeURIComponent(personaId)}/manuales`),
  registrarManual: (personaId, datos) => api.post(`/osint/persona/${encodeURIComponent(personaId)}/manuales`, datos),
  investigate: (personaId, options = {}) => api.post(`/osint/persona/${encodeURIComponent(personaId)}/investigations`,
    typeof options === 'string' ? { query: options } : options, { timeout: 180000 })
};

export default api;

